import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { eventAccessApi } from "./eventAccessApi";
import listStyles from "../pages/AllEnquiries.module.scss";

const PER_PAGE = 20;

export default function EventAccessTable({ eventId, onGenerateDone }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await eventAccessApi.listEventAccess(eventId, {
        page,
        per_page: PER_PAGE,
      });
      if (res?.error) {
        toast.error(res.error);
        setItems([]);
        setTotal(0);
        return;
      }
      setItems(res?.items ?? []);
      setTotal(res?.total ?? 0);
    } catch (e) {
      toast.error(e?.message || "Failed to load access list");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [eventId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSendQr = async (accessId, isResend) => {
    if (!accessId) return;
    setSendingId(accessId);
    try {
      const res = await eventAccessApi.sendQr(accessId);
      if (res?.error) {
        toast.error(res.error);
        return;
      }
      toast.success(res?.message || (isResend ? "QR resent." : "QR sent."));
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to send");
    } finally {
      setSendingId(null);
    }
  };

  const handleBulkSend = async () => {
    if (!eventId) return;
    setBulkSending(true);
    try {
      const res = await eventAccessApi.sendAllQr(eventId);
      if (res?.error) {
        toast.error(res.error);
        return;
      }
      const sent = res?.sent ?? 0;
      const errors = res?.errors ?? [];
      if (errors.length > 0) {
        toast.warning(`Sent ${sent} QR code(s). Some failed: ${errors.slice(0, 2).join("; ")}${errors.length > 2 ? "…" : ""}`);
      } else {
        toast.success(res?.message || `Sent ${sent} QR code(s).`);
      }
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to send");
    } finally {
      setBulkSending(false);
    }
  };

  const handleGenerate = async () => {
    if (!eventId) return;
    setGenerating(true);
    try {
      const res = await eventAccessApi.generateAccess(eventId);
      if (res?.error) {
        toast.error(res.error);
        return;
      }
      const count = res?.generated ?? 0;
      toast.success(`Generated ${count} QR code(s).`);
      if (onGenerateDone) onGenerateDone();
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div>
      <Box className={listStyles.header} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {total} attendee{total !== 1 ? "s" : ""}
        </Typography>
        <Box className={listStyles.headerActions}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleBulkSend}
            disabled={bulkSending || items.length === 0}
          >
            {bulkSending ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Sending…
              </>
            ) : (
              "Bulk Send QR"
            )}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Generating…
              </>
            ) : (
              "Generate QR Codes"
            )}
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Spinner animation="border" size="sm" />
        </Box>
      ) : (
        <>
          <Paper variant="outlined" className={listStyles.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Student</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>QR Sent</strong></TableCell>
                  <TableCell><strong>Used</strong></TableCell>
                  <TableCell><strong>Used At</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted">
                      No attendees yet, or no access tokens. Add participants and click “Generate QR Codes”.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <TableRow key={row.enquiry_id || row.access_id || row.email} className={listStyles.tableRow}>
                      <TableCell>
                        <Typography variant="body2" className={listStyles.primaryText}>
                          {row.student_name || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell className={listStyles.secondaryText}>{row.email || "—"}</TableCell>
                      <TableCell>
                        {row.qr_sent ? (
                          <Typography component="span" variant="body2" sx={{ color: "success.main", fontWeight: 500 }}>Sent</Typography>
                        ) : (
                          <Typography component="span" variant="body2" color="text.secondary">Pending</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.is_used ? (
                          <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>Used</Typography>
                        ) : (
                          <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell className={listStyles.secondaryText}>
                        {row.used_at
                          ? new Date(row.used_at).toLocaleString(undefined, {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {row.access_id ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSendQr(row.access_id, !!row.qr_sent)}
                            disabled={sendingId === row.access_id || row.is_used}
                            title={row.qr_sent ? "Resend QR" : "Send QR"}
                          >
                            {sendingId === row.access_id ? (
                              <Spinner animation="border" size="sm" />
                            ) : row.qr_sent ? (
                              "Resend QR"
                            ) : (
                              "Send QR"
                            )}
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No token</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
          {!loading && items.length > 0 && (
            <Box className={listStyles.pagination} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing page {page} of {totalPages}
              </Typography>
              {totalPages > 1 ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {page} / {totalPages}
                  </Typography>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </Box>
              ) : (
                <span />
              )}
            </Box>
          )}
        </>
      )}
    </div>
  );
}
